export const STORAGE_NAME = "recentProducts";
const STORAGE_AMOUNT = 5;
const WEEK_IN_MILLISECONDS = 604800000;

type RecentProductStorage = {
  productId: string;
  viewedAt: number;
};

const useRecentProducts = (currentProductId: Ref<string | undefined>) => {
  const storage = useStorageSafely<RecentProductStorage[]>({
    type: "localStorage",
    key: STORAGE_NAME,
    defaultValue: [],
  });

  onBeforeMount(() => {
    const currentDate = new Date().getTime();

    storage.value = storage.value.filter(
      // See if we viewed this item more than a week ago, if so filter it out
      (storageItem) =>
        currentDate - storageItem.viewedAt <= WEEK_IN_MILLISECONDS,
    );
  });

  // Save the product id the user is currently to the storage
  onMounted(() => {
    if (!currentProductId?.value) return;

    // Copy storage so we don't directly manipulate the values
    let storageCopy = [...storage.value];

    // Remove value if it's already in the array
    const doubleValueIndex = storageCopy.findIndex(
      (storageItem) => storageItem.productId === currentProductId.value,
    );
    if (doubleValueIndex > -1) {
      storageCopy.splice(doubleValueIndex, 1);
    }

    // Move new value to the top
    storageCopy.unshift({
      productId: currentProductId.value,
      // Get current date
      viewedAt: new Date().getTime(),
    });

    // Ensure we save 10 (max) + 1 product (for product detail)
    storageCopy = storageCopy.slice(0, STORAGE_AMOUNT + 1);

    // Set the new storage values
    storage.value = storageCopy;
  });

  return computed(() =>
    storage.value
      // Filter out the current product id
      .filter((item) => item.productId !== currentProductId?.value)
      // Only return product ids, not viewed at
      .map((item) => item.productId)
      // Return the first 10 products
      .slice(0, STORAGE_AMOUNT),
  );
};

export default useRecentProducts;
